import axios, {AxiosError} from 'axios';
import fileDownload from 'js-file-download';
import {toast} from 'react-toastify';
import {call, ForkEffect, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {ActionCreators} from 'redux-undo';
import i18n, {type TypedTranslation} from '../../i18n';
import {IPresentation} from '../../incase-domain/presentation';
import {
    downloadPresentationVerbose,
    fetchPresentationVerbose,
    getPresentationProtection,
    updatePresentation,
    updatePresentationProtection,
} from '../../service/presentation';
import {fetchPresentationThemeService} from '../../service/presentationTheme.service';
import {addSlides, copyPresentationSlide, saveSlideSwap} from '../../service/slides';
import createSlideTemplate from '../../service/template/template';
import {ADD_SLIDES, SWAP_SELECTED_SLIDES} from '../actions/edit';
import {
    DELETE_EMITTED_SHARED_PRESENTATION_ACTION,
    EXPORT_PRESENTATION,
    exportPresentationEndAction,
    FETCH_PRESENTATION_PROTECTION,
    SELECT_PRESENTATION_TO_EDIT,
    setDeletedEmittedSharedPresentationAction,
    setEmittedSharedPresentationAction,
    UPDATE_PRESENTATION_PROTECTION,
    UPDATE_PRESENTATION_WITH_EMITTED_PRESENTATION_ACTION,
    UPDATE_SHARED_PRESENTATION_WITH_EMITTED_SHARED_PRESENTATION_ACTION,
    updateSelectedPresentationAction,
    updateSelectedPresentationSlideIndexListAction,
    updateSelectedPresentationSlidesAction,
    updateSettingsConfigAction,
} from '../actions/presentation';
import {CREATE_SLIDE_COPY, updateSelectedSlideAction} from '../actions/slide';
import {appendSlidePreviewCacheBust} from '../../utils/slidePreviewUrl';
import {getSlidePreviews} from '../../service/slide.preview';
import {hideLoadingSpinnerAction, showLoadingSpinnerAction, updateSelectedSlidePreviewsAction} from '../actions/slide.preview';
import {startPageSpinnerAction, startPresUpdateSpinnerAction, stopPageSpinnerAction, stopPresUpdateSpinnerAction} from '../actions/spinner';
import {IAddSlidesAction, ISwapSelectedSlidesAction} from '../actions/types/edit.types';
import {
    IDeleteEmittedSharedPresentationAction,
    IExportPresentationAction,
    IFetchPresentationProtectionAction,
    ISelectPresentationToEditAction,
    IUpdatePresentationProtection,
    IUpdatePresentationWithEmittedPresentationAction,
    IUpdateSharedPresentationWithEmittedSharedPresentationAction,
} from '../actions/types/presentation.types';
import {ICreateSlideCopyAction} from '../actions/types/slide.types';
import {QUERY_KEYS} from '../../utils/queryKeys';
import {queryClient} from '../../utils/queryClient';
import {AppState} from '../reducer';
import {ISlidePreview} from '../../incase-domain/proptypes/slidePreview';

export function* selectPresentationToEditSaga(action: ISelectPresentationToEditAction): unknown {
    const {presentationId, template, navigate} = action.payload;

    yield put(startPageSpinnerAction());

    try {
        const presentationToEdit: IPresentation = yield call(fetchPresentationVerbose, presentationId);
        const presentationToEditSlides = presentationToEdit.slides || [];

        yield put(updateSelectedPresentationAction({...presentationToEdit, slides: []}));
        yield put(updateSelectedPresentationSlidesAction(presentationToEditSlides));
        yield put(updateSelectedPresentationSlidesAction(presentationToEditSlides));

        if (template) {
            const slide = presentationToEdit.slides[0];
            const slideWithTemplate = {
                ...presentationToEdit.slides[0],
                elements: [...slide.elements, ...createSlideTemplate(template, slide._id)],
            };
            yield put(updateSelectedPresentationSlidesAction([slide]));

            yield put(updateSelectedSlideAction(slideWithTemplate, 0, false));
        } else {
            yield put(updateSelectedSlideAction(presentationToEdit.slides[0], 0, false));
        }

        yield put(showLoadingSpinnerAction());
        const slidePreviews: ISlidePreview[] = yield call(getSlidePreviews, presentationToEdit.slideIndexList);
        yield put(
            updateSelectedSlidePreviewsAction(
                slidePreviews.map((preview) => ({...preview, imgSrc: appendSlidePreviewCacheBust(preview.imgSrc, Date.now())}))
            )
        );
        yield put(hideLoadingSpinnerAction());
        yield put(ActionCreators.clearHistory());

        if (presentationToEdit.presentationThemeId) {
            const themeId = presentationToEdit.presentationThemeId;
            yield call(() =>
                queryClient.prefetchQuery({
                    queryKey: QUERY_KEYS.theme.detail(themeId),
                    queryFn: () => fetchPresentationThemeService(themeId),
                })
            );
        }
    } catch (e) {
        if (axios.isAxiosError(e)) {
            const axiosError: AxiosError = e;
            if (
                axiosError.response &&
                typeof axiosError.response.data === 'object' &&
                axiosError.response.data !== null &&
                'message' in axiosError.response.data
            ) {
                toast.warning(i18n.t(axiosError.response.data.message as TypedTranslation));
            }
        }

        navigate('/');
    }

    yield put(stopPageSpinnerAction());
}

export function* exportPresentationSaga(action: IExportPresentationAction): unknown {
    try {
        const response = yield call(downloadPresentationVerbose, action.presentationId);
        const exportName = decodeURIComponent(response.headers['x-filename']);
        fileDownload(response.data, exportName);
        toast.success(`${i18n.t('toasts.presentation')} ${exportName} ${i18n.t('toasts.exportPresentation')}`);
        yield put(exportPresentationEndAction(action.presentationId));
    } catch (e) {
        toast.error(i18n.t('toasts.errorOccurred'));
        yield put(exportPresentationEndAction(action.presentationId));
    }
}

export function* saveSwapSlidesSaga(action: ISwapSelectedSlidesAction): unknown {
    const {newIndex, oldIndex} = action.indices;

    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;
    const {selectedSlide, selectedSlideIndex} = state.slideReducer.present;

    try {
        const updatedPresentation: IPresentation = yield call(saveSlideSwap, selectedPresentation._id, newIndex, oldIndex);

        if (selectedSlideIndex === oldIndex) {
            put(updateSelectedSlideAction(selectedSlide, newIndex, false));
        } else if (newIndex <= selectedSlideIndex && oldIndex > selectedSlideIndex) {
            put(updateSelectedSlideAction(selectedSlide, selectedSlideIndex + 1, false));
        } else if (newIndex >= selectedSlideIndex && oldIndex < selectedSlideIndex) {
            put(updateSelectedSlideAction(selectedSlide, selectedSlideIndex - 1, false));
        }

        yield put(
            updateSelectedPresentationAction({
                ...selectedPresentation,
                slideIndexList: updatedPresentation.slideIndexList,
            })
        );
    } catch (e) {
        toast.success(i18n.t('toasts.errorOccurred'));
    }
}

export function* addSlidesSaga(action: IAddSlidesAction): unknown {
    const {slidesToAdd, hidden, template, toEnd} = action;
    yield put(startPresUpdateSpinnerAction(true));

    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;
    const {selectedSlideIndex} = state.slideReducer.present;

    const newSlidePosition = action.toEnd ? selectedPresentation.slideIndexList.length + 1 : selectedSlideIndex + 1;

    const presentation: IPresentation = yield call(addSlides, selectedPresentation._id, slidesToAdd, newSlidePosition, hidden);

    if (presentation.newSlides) {
        const newSlide = presentation.newSlides[0];
        const newSlideWithTemplate = {
            ...newSlide,
            elements: [...newSlide.elements, ...createSlideTemplate(template, newSlide._id)],
        };

        const updatedSlides = selectedPresentation.slides;
        updatedSlides.splice(newSlidePosition, 0, ...presentation.newSlides);
        yield put(updateSelectedPresentationSlidesAction(updatedSlides));
        yield put(updateSelectedPresentationSlideIndexListAction(presentation.slideIndexList));
        yield put(
            updateSelectedSlideAction(newSlideWithTemplate, toEnd ? presentation.slideIndexList.length - 1 : selectedSlideIndex + 1, false)
        );
    }

    yield put(stopPresUpdateSpinnerAction());
}

export function* createSlideCopySaga(action: ICreateSlideCopyAction): unknown {
    try {
        yield put(startPresUpdateSpinnerAction(true));
        const state: AppState = yield select();
        const {selectedPresentation, selectedPresentationSlidePreviews} = state.presentationReducer;

        const copySlide = action.slide;
        const result = yield call(copyPresentationSlide, copySlide);

        if (result._id) {
            const copiedSlideIndex = selectedPresentation.slideIndexList.findIndex((id) => id === copySlide._id);
            const newSlideIndexList = [...selectedPresentation.slideIndexList];
            newSlideIndexList.splice(copiedSlideIndex + 1, 0, result._id);

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const {slides, ...presentationMeta} = selectedPresentation;
            presentationMeta.slideIndexList = [...newSlideIndexList];

            const updatedPresentation = yield call(updatePresentation, presentationMeta);

            const newSelectedPresentationSlides = [...selectedPresentation.slides];
            newSelectedPresentationSlides.splice(copiedSlideIndex + 1, 0, result);

            yield put(
                updateSelectedPresentationAction({
                    ...updatedPresentation,
                    slides: newSelectedPresentationSlides,
                })
            );

            let copiedSlidePreview = selectedPresentationSlidePreviews.find((preview) => preview.slideId === copySlide._id);
            if (copiedSlidePreview) {
                copiedSlidePreview = {
                    ...copiedSlidePreview,
                    slideId: result._id,
                    imgSrc: appendSlidePreviewCacheBust(copiedSlidePreview.imgSrc, Date.now()),
                };
                yield put(updateSelectedSlidePreviewsAction([...selectedPresentationSlidePreviews, copiedSlidePreview]));
            }
            yield put(updateSelectedSlideAction(result, copiedSlideIndex + 1, true));
        }
        yield put(stopPresUpdateSpinnerAction());
    } catch (error) {
        yield put(stopPresUpdateSpinnerAction());
    }
}

export function* updatePresentationProtectionSaga(action: IUpdatePresentationProtection): unknown {
    yield put(startPresUpdateSpinnerAction(true));

    try {
        yield call(updatePresentationProtection, action.presentationId, action.protection);
        yield put(updateSettingsConfigAction(action.protection));
        yield call(toast.success, i18n.t('toasts.successfullySaved'));
    } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 400) {
            const responseData = e.response.data;
            let messageKey = (<AxiosError<{message: string}>>e).response?.data.message as string;

            if (responseData && typeof responseData === 'object') {
                const firstProp = Object.values(responseData)[0];
                if (Array.isArray(firstProp) && firstProp.length > 0) {
                    [messageKey] = firstProp;
                }
            }

            yield call(toast.error, i18n.t(messageKey as TypedTranslation));
        } else {
            yield call(toast.error, i18n.t('toasts.notSavedTryAgain'));
        }
    } finally {
        yield put(stopPresUpdateSpinnerAction());
    }
}

export function* fetchPresentationProtectionSaga(action: IFetchPresentationProtectionAction): unknown {
    yield put(startPresUpdateSpinnerAction(true));
    const protection = yield call(getPresentationProtection, action.presentationId);
    yield put(updateSettingsConfigAction(protection));
    yield put(stopPresUpdateSpinnerAction());
}

export function* updatePresentationWithEmittedPresentationSaga(action: IUpdatePresentationWithEmittedPresentationAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;

    if (action.presentation) {
        yield put(
            updateSelectedPresentationAction({
                ...selectedPresentation,
                ...action.presentation,
                slides: selectedPresentation.slides,
            })
        );
    }
    if (action.labels) {
        yield put(
            updateSelectedPresentationAction({
                ...selectedPresentation,
                languageConfig: {labels: action.labels},
            })
        );
    }
    if (action.protection) {
        yield put(updateSettingsConfigAction(action.protection));
    }
}

export function* updateSharedPresentationWithEmittedSharedPresentationSaga(
    action: IUpdateSharedPresentationWithEmittedSharedPresentationAction
): unknown {
    if (action.sharedPresentation) {
        yield put(setEmittedSharedPresentationAction(action.sharedPresentation));
    }
}

export function* deleteEmittedSharedPresentationSaga(action: IDeleteEmittedSharedPresentationAction): unknown {
    if (action.presentationIds) {
        yield put(setDeletedEmittedSharedPresentationAction(action.presentationIds));
    }
}

export function* watchPresentationSaga(): Generator<ForkEffect<never>, void> {
    yield takeLatest(SELECT_PRESENTATION_TO_EDIT, selectPresentationToEditSaga);
    yield takeEvery(EXPORT_PRESENTATION, exportPresentationSaga);
    yield takeLatest(SWAP_SELECTED_SLIDES, saveSwapSlidesSaga);
    yield takeLatest(ADD_SLIDES, addSlidesSaga);
    yield takeLatest(CREATE_SLIDE_COPY, createSlideCopySaga);
    yield takeLatest(UPDATE_PRESENTATION_PROTECTION, updatePresentationProtectionSaga);
    yield takeLatest(FETCH_PRESENTATION_PROTECTION, fetchPresentationProtectionSaga);
    yield takeLatest(UPDATE_PRESENTATION_WITH_EMITTED_PRESENTATION_ACTION, updatePresentationWithEmittedPresentationSaga);
    yield takeLatest(
        UPDATE_SHARED_PRESENTATION_WITH_EMITTED_SHARED_PRESENTATION_ACTION,
        updateSharedPresentationWithEmittedSharedPresentationSaga
    );
    yield takeLatest(DELETE_EMITTED_SHARED_PRESENTATION_ACTION, deleteEmittedSharedPresentationSaga);
}
